import type { Row, SortingState } from "@tanstack/react-table";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import ToggleTableVisibilityButton from "modules/humanResources/components/ToggleTableVisibilityButton";
import { useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import BreadcrumbsSection from "~/components/Breadcrumbs/BreadcrumbsSection";
import SearchInput from "~/components/formElements/SearchInput/SearchInput";
import Headline from "~/components/Headline/Headline";
import Section from "~/components/sections/Section";
import Table from "~/components/Table/Table";
import { HR_STAFF_MEMBER_DETAILS_PATH_WITH_TAB_ID } from "~/constants/appRoutes.ts";
import type { BenefitReportResponse } from "~/modules/humanResources/api/staffMemberBenefit/staffMemberBenefitTypes.ts";
import HrMainPageTabs from "~/modules/humanResources/components/HrMainPageTabs";
import {
	getBenefitsReportColumnDefs,
} from "~/modules/humanResources/components/StaffBenefitsReportView/benefitsReportColumnDefs.tsx";
import type {
	BenefitsReportTableData,
} from "~/modules/humanResources/components/StaffBenefitsReportView/benefitsReportTableTypes.ts";
import DownloadBenefitsReportExcelButton
	from "~/modules/humanResources/components/StaffBenefitsReportView/components/DownloadBenefitsReportExcelButton/DownloadBenefitsReportExcelButton";
import CompensationSectionLockedMessage
	from "~/modules/humanResources/components/StaffMemberDetailsView/components/CompensationSectionLockedMessage";
import { StaffMemberDetailsTabId } from "~/modules/humanResources/types/staffMemberDetailsTypes.ts";
import LoadingPage from "~/pages/LoadingPage.tsx";
import { useStaffMemberCompensationVisibilityStore } from "~/stores/useStaffMemberCompensationVisibilityStore.ts";

type Props = {
	benefitsReportResponseData?: BenefitReportResponse;
	isLoading: boolean;

};

const StaffBenefitsReportView = ({
	benefitsReportResponseData,
	isLoading,
}: Props) => {

	const navigate = useNavigate();
	const [sorting, setSorting] = useState<SortingState>([{ id: "fullName", desc: false }]);
	const [globalFilter, setGlobalFilter] = useState<string>("");
	const {
		isVisible: tableIsVisible,
		setVisibility: setTableVisibility,
	} = useStaffMemberCompensationVisibilityStore((state) => state);

	const handleRowClick = useCallback((row: Row<BenefitsReportTableData>) => {
		navigate(generatePath(HR_STAFF_MEMBER_DETAILS_PATH_WITH_TAB_ID, {
			staffMemberId: row.original.id,
			tabId: StaffMemberDetailsTabId.Compensation,
		}));
	}, [navigate]);

	const tableData = useMemo(() => {
		if (benefitsReportResponseData) {
			return benefitsReportResponseData.reportEntries;
		}
		return [];
	}, [benefitsReportResponseData]);

	const columnDefs = useMemo(() => {
		if (benefitsReportResponseData) {
			return getBenefitsReportColumnDefs(benefitsReportResponseData.benefitDescriptions.sort());
		}
		return [];
	}, [benefitsReportResponseData]);

	const table = useReactTable({
		data: tableData,
		columns: columnDefs,
		state: {
			sorting,
			globalFilter,
			columnPinning: {
				left: ["fullName"],
			},
		},
		meta: {
			onRowClick: handleRowClick,
			emptyTableDataMessage: "Es wurden keine aktiven Mitarbeiter:innen gefunden.",
		},
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel:getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
	});

	return (
		<div className="grid h-screen grid-rows-[auto_auto_auto_1fr] overflow-hidden">
			<BreadcrumbsSection pages={["HR", "Reports"]}
								className="bg-white" />
			<Section className="bg-white pb-4 pt-2">
				<div className="flex w-full items-center gap-x-2">
					<Headline type="h2">Benefits-Report</Headline>

					<div className="ml-auto flex items-center justify-between gap-x-2">
						<SearchInput value={globalFilter} onChange={setGlobalFilter} placeholder="Mitarbeiter/in"/>
						<ToggleTableVisibilityButton tableIsVisible={tableIsVisible}
													 setTableIsVisible={setTableVisibility} />

						<DownloadBenefitsReportExcelButton downloadDisabled={isLoading || !tableIsVisible}
														   data={tableData}
														   benefitColNames={benefitsReportResponseData?.benefitDescriptions || []}
						/>
					</div>
				</div>

			</Section>
			<Section>
				<HrMainPageTabs selectedTabName={"benefits-report"} />
			</Section>

			<div className="mx-auto size-full max-w-7xl overflow-hidden px-2 pb-10 pt-4 sm:px-6 lg:px-8">
				{!tableIsVisible &&
					<CompensationSectionLockedMessage setVisibility={setTableVisibility} />}
				{tableIsVisible && isLoading && <LoadingPage />}
				{tableIsVisible && !isLoading &&
					<Table table={table} />
				}
			</div>
		</div>
);
};
export default StaffBenefitsReportView;